/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Oct 2021
 * @flow
 */

import React from 'react';
import { connect } from 'react-redux';

import View from './views';
import API from '../../requests';
import FireboardUtils from '../../util/fireboard-util';
import { getPopupConfig } from '../../components/Popup';
import EventEmitter from '../../util/event-emitter';
import strings from '../../globalization';

class TableSensors extends React.Component {
  state = {
    popup: undefined,
    sensorsMap: new Map(),
    stationsWithSensors: [],
    stationsWithoutSensors: [],
  };

  sensorGroupsList = [];
  selectedSensorGroup = null;
  selectedStation = null;
  scanQRCodeListener = null;

  componentDidMount() {
    const { reduxProps: { storeInfo } } = this.props;
    const { id: storeId, sensorGroups } = storeInfo?.data || {};
    this.sensorGroupsList = sensorGroups;
    this.getSensors(storeId);

    if (window.ReactNativeWebView) {
      this.scanQRCodeListener = message => this.onQRCodeScanned(message);
      EventEmitter.addListeners('onQRCodeScanned', this.scanQRCodeListener);
    }
  }

  componentWillUnmount() {
    if (window.ReactNativeWebView) {
      this.scanQRCodeListener && EventEmitter.removeListeners('onQRCodeScanned', this.scanQRCodeListener);
    }
  }

  getSensors = storeId => {
    this.getPopup('spinner', 'Fetching Table Sensors', null);
    API.getSensors(storeId)
      .then(response => {
        const sensorsData = response.data;
        let sensorsMap = new Map();
        sensorsData.forEach(sensor => {
          if (sensor.stationId) {
            if (sensorsMap.has(sensor.stationId)) {
              const exitingSensors = sensorsMap.get(sensor.stationId);
              sensorsMap.set(sensor.stationId, [...exitingSensors, sensor]);
            } else {
              sensorsMap.set(sensor.stationId, [sensor]);
            }
          }
        });
        this.setState({ sensorsMap, popup: undefined }, () => {
          this.filterStations();
        });
        FireboardUtils.addNetworkLogs(response);
      })
      .catch(error => this.getErrorPopup(error));
  };

  filterStations = () => {
    const { reduxProps } = this.props;
    const { storeInfo } = reduxProps || {};
    const { stations } = storeInfo?.data || {};
    const stationsArray = Array.isArray(stations) ? stations : [];
    const stationsWithSensors = stationsArray.filter(station => this.withStationFilterFn(station));
    const stationsWithoutSensors = stationsArray.filter(station => this.withoutStationFilterFn(station));
    this.setState({ stationsWithSensors, stationsWithoutSensors });
  };

  withStationFilterFn = station => {
    const { sensorsMap } = this.state;
    if (sensorsMap.has(station.id)) {
      const sens = sensorsMap.get(station.id);
      return Array.isArray(sens) && sens.length > 0;
    }
    return false;
  };

  withoutStationFilterFn = station => {
    const { sensorsMap } = this.state;
    if (sensorsMap.has(station.id)) {
      const sens = sensorsMap.get(station.id);
      return Array.isArray(sens) && sens.length === 0;
    }
    return true;
  };

  pad = (num, size) => {
    let str = String(num);
    while (str.length < (size || 10)) {
      str = '0' + str;
    }
    return str;
  };

  sortFunction = (a, b) => {
    const name1 = this.pad(a.name ? a.name : '', 10).toLowerCase();
    const name2 = this.pad(b.name ? b.name : '', 10).toLowerCase();
    if (name1 < name2) {
      return -1;
    }
    if (name1 > name2) {
      return 1;
    }
    return 0;
  };

  getPopup = (type, heading, message, callback) => {
    const popup =
      type === 'spinner'
        ? getPopupConfig('spinner')
        : getPopupConfig('error', () => {
          callback && callback();
          this.setState({ popup: undefined });
        });
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  closePopup = () => {
    this.setState({ popup: undefined });
  };

  getErrorPopup = (error) => {
    const errorMsg = error.response
      ? error.response.data
      : error.message;
    FireboardUtils.addNetworkLogs(error.response);
    this.getPopup('error', null, errorMsg);
  };

  goBack = () => {
    const { history } = this.props;
    history && history.goBack();
  };

  onClickAddSensor = station => {
    this.selectedStation = station;
    this.selectedSensorGroup = null;
    if (Array.isArray(this.sensorGroupsList) && this.sensorGroupsList.length > 0) {
      this.setState({ popup: { type: 'sensorGroup' } });
    } else if (window.ReactNativeWebView) {
      EventEmitter.scanQRCode('scanQRCode');
    }
  };

  openQRCodeScanner = selectedSensorGroup => {
    this.closePopup();
    this.selectedSensorGroup = selectedSensorGroup;
    if (window.ReactNativeWebView) {
      EventEmitter.scanQRCode('scanQRCode');
    }
    else{
      this.setState({ popup: { type: 'openScanner' } });
    }
  };

  onQRCodeScanned = qrCode => {
    const callback = () => {
      this.getPopup('spinner', strings.addingSensor, null);
      const { reduxProps: { storeInfo } } = this.props;
      const { id: storeId } = storeInfo?.data || {};
      const payload = {
        storeId: storeId,
        externalId: FireboardUtils.getLastCodeFromQrCode(qrCode),
        sensorTypeId: 1,
        stationId: this.selectedStation?.id,
        sensorGroupId: this.selectedSensorGroup ? this.selectedSensorGroup?.id : null,
      };
      API.registerSensor(payload)
        .then(response => {
          let { sensorsMap } = this.state;
          if (sensorsMap.has(response.data.stationId)) {
            const existingSensors = sensorsMap.get(response.data.stationId); //pull out existing sensors array
            sensorsMap.set(response.data.stationId, [...existingSensors, response.data]); //append new sensor
          } else {
            sensorsMap.set(response.data.stationId, [response.data]);
          }
          this.setState({ sensorsMap, popup: undefined }, () => {
            this.filterStations();
          });
          FireboardUtils.addNetworkLogs(response);
        })
        .catch(error => this.getErrorPopup(error));
    };

    const popup = getPopupConfig('error');
    popup.heading = 'Confirmation';
    popup.message = `This will assign the sensor to table ${this.selectedStation.name}. Do you want to continue?`;
    popup.buttons = [
      {
        text: 'NO',
        onClick: this.closePopup,
      },
      {
        text: 'YES',
        onClick: () => {
          callback && callback();
        }
      },
    ];
    this.setState({ popup });
  };

  onClickDeleteSensor = sensor => {
    this.deleteSensor(sensor);
  };

  deleteSensor = sensor => {
    const callback = () => {
      this.getPopup('spinner', strings.deletingSensor, null);
      API.deRegisterSensor(sensor.id)
        .then(response => {
          const { sensorsMap } = this.state;
          const existingSensors = sensorsMap.get(sensor.stationId);
          const newSensors = existingSensors.filter(s => s.id !== sensor.id);
          sensorsMap.set(sensor.stationId, [...newSensors]);
          this.setState({ sensorsMap, popup: undefined }, () => {
            this.filterStations();
          });
          FireboardUtils.addNetworkLogs(response);
        })
        .catch(error => this.getErrorPopup(error));
    };

    const { stationsWithSensors } = this.state;
    const station = stationsWithSensors.find(st => st.id === sensor.stationId);
    const stationName = station?.name;
    const popup = getPopupConfig('error');
    popup.heading = 'Confirmation';
    popup.message = `This will delete the sensor from table ${stationName}. Do you want to continue?`;
    popup.buttons = [
      {
        text: 'NO',
        onClick: this.closePopup,
      },
      {
        text: 'YES',
        onClick: () => {
          callback && callback();
        }
      },
    ];
    this.setState({ popup });
  };

  get viewProps() {
    const {
      popup,
      sensorsMap,
      stationsWithSensors,
      stationsWithoutSensors
    } = this.state || {};

    const noSensorGroup = { id: null, name: "No Sensor Group" };
    const sensorGroups = [noSensorGroup, ...this.sensorGroupsList];
    return {
      popup,
      sensorsMap,
      stationsWithSensors,
      stationsWithoutSensors,
      sensorGroupsList: sensorGroups,
      goBack: this.goBack,
      closePopup: this.closePopup,
      sortFunction: this.sortFunction,
      onClickAddSensor: this.onClickAddSensor,
      onClickDeleteSensor: this.onClickDeleteSensor,
      openQRCodeScanner: this.openQRCodeScanner,
      onQRCodeScanned: this.onQRCodeScanned,
      selectedStation: this.selectedStation
    };
  };

  render() {
    const viewProps = this.viewProps;
    return <View {...viewProps} />;
  }
}

const mapStateToProps = (reduxProps) => ({
  reduxProps,
});

export default connect(mapStateToProps, null)(TableSensors);
